/**
* @license Apache-2.0
*
* Copyright (c) 2024 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isObject from '@stdlib/assert-is-plain-object';
import hasOwnProp from '@stdlib/assert-has-own-property';
import contains from '@stdlib/array-base-assert-contains';
import format from '@stdlib/error-tools-fmtprodmsg';


// MAIN //

/**
* Validates function options.
*
* @private
* @param {Object} opts - destination object
* @param {Array} dtypes - list of supported output data types
* @param {Options} options - function options
* @param {string} [options.dtype] - output array data type
* @returns {(Error|null)} null or an error object
*
* @example
* var opts = {};
* var dtypes = [ 'float64', 'float32', 'generic' ];
* var options = {
*     'dtype': 'float64'
* };
* var err = validate( opts, dtypes, options );
* if ( err ) {
*     throw err;
* }
*/
function validate( opts, dtypes, options ) {
	if ( !isObject( options ) ) {
		return new TypeError( format( '1qj2V', options ) );
	}
	if ( hasOwnProp( options, 'dtype' ) ) {
		opts.dtype = options.dtype;
		if ( !contains( dtypes, opts.dtype ) ) {
			return new TypeError( format( '1qj4S', 'dtype', dtypes.join( '", "' ), opts.dtype ) );
		}
	}
	return null;
}


// EXPORTS //

export default validate;
